import { useState, useRef, useCallback, useEffect, useMemo } from "react"

const LOADING_LABELS: Record<string, string> = {
  tiles: "de la carte",
  style: "du style",
  parcel: "de la parcelle",
}

const getLoadingLabel = (key: string): string => {
  if (LOADING_LABELS[key]) return LOADING_LABELS[key]
  if (key.startsWith("layer-")) return `du calque ${key.replace("layer-", "")}`
  return key
}

const MAP_LOADING_PREFIXES = ["tiles", "style", "layer-", "parcel"]
const isMapKey = (key: string) => MAP_LOADING_PREFIXES.some((p) => key.startsWith(p))

export const useMapLoading = () => {
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set())
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const moveStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setLoading = useCallback((key: string, active: boolean) => {
    setLoadingKeys((prev) => {
      const next = new Set(prev)
      if (active) next.add(key)
      else next.delete(key)
      return next
    })
  }, [])

  const onMoveStart = useCallback(() => {
    if (moveStartTimeoutRef.current) clearTimeout(moveStartTimeoutRef.current)
    moveStartTimeoutRef.current = setTimeout(() => {
      moveStartTimeoutRef.current = null
      setLoading("tiles", true)
    }, 150)
  }, [setLoading])

  const onMapIdle = useCallback(() => {
    if (moveStartTimeoutRef.current) {
      clearTimeout(moveStartTimeoutRef.current)
      moveStartTimeoutRef.current = null
    }
    setLoadingKeys((prev) => {
      const next = new Set<string>(Array.from(prev).filter((key) => !isMapKey(key)))
      return next.size === prev.size ? prev : next
    })
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current)
      loadingTimeoutRef.current = null
    }
  }, [])

  // Safety timeout: auto-clear loading after 30s
  useEffect(() => {
    if (loadingKeys.size > 0) {
      loadingTimeoutRef.current = setTimeout(() => {
        setLoadingKeys(new Set())
      }, 30000)
      return () => {
        if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current)
      }
    }
  }, [loadingKeys.size > 0])

  const displayMessage = useMemo(() => {
    const labels = Array.from(loadingKeys).map(getLoadingLabel)
    if (labels.length === 0) return ""
    if (labels.length === 1) return `Chargement ${labels[0]}...`
    if (labels.length === 2) return `Chargement ${labels[0]} et ${labels[1]}...`
    return `Chargement de ${labels.length} elements...`
  }, [loadingKeys])

  return {
    isLoading: loadingKeys.size > 0,
    displayMessage,
    setLoading,
    onMoveStart,
    onMapIdle,
  }
}
